import {v4 as uuidv4} from 'uuid';
import * as Sentry from '@sentry/react';
import {securedFetchRequest} from '@bidmii/common/lib/util/FetchRequest';
import {CDN_URL, API_URL} from '../constants';
import {getStorage, ref, uploadBytesResumable} from 'firebase/storage';
import {convertHeicToJpeg} from './ConvertToPng';

// Lazy-load storage and firebaseApp to avoid initialization errors
let storage: ReturnType<typeof getStorage> | null = null;
const getStorageInstance = async () => {
  if (!storage) {
    // Lazy import firebaseApp to avoid circular dependency issues
    const {firebaseApp} = await import('../App');
    storage = getStorage(firebaseApp);
  }
  return storage;
};
export const storeRef = (attachmentReference: $TSFixMe) => {
  return securedFetchRequest(`${API_URL}/attachments`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(attachmentReference),
  });
};

export const deleteRef = (attachmentReference: $TSFixMe) => {
  return securedFetchRequest(
    `${API_URL}/attachments/${attachmentReference.id}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
};

// Fired on window whenever a handleFiles batch finishes with at least one
// failed file; detail.message is ready to show to the user.
export const UPLOAD_STATUS_EVENT = 'bidmii-upload-status';

// Give up on uploads still running after this long; the user is told to retry
// on a better connection and the stuck uploads are cancelled so a retry can
// never produce a duplicate.
const UPLOAD_TIMEOUT_MS = 60000;
// If a save request hangs past the give-up point, force-finalize anyway so the
// caller's loading state is always released.
const FINALIZE_BACKSTOP_MS = 90000;
// At or above this size, the failure message leads with the file's size.
const LARGE_FILE_MB = 8;

const PHOTO_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'heic',
  'heif',
  'bmp',
  'svg',
];
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'webm', 'mkv', 'm4v'];

const fileNoun = (extension: string) => {
  if (PHOTO_EXTENSIONS.includes(extension)) {
    return 'photo';
  }
  if (VIDEO_EXTENSIONS.includes(extension)) {
    return 'video';
  }
  return 'file';
};

type UploadOutcome = {
  name: string;
  sizeMB: number;
  noun: string;
  status: 'pending' | 'saved' | 'failed' | 'canceled';
  ref: $TSFixMe;
  task: $TSFixMe;
};

export type UploadSummary = {
  total: number;
  saved: number;
  failed: number;
  timedOut: boolean;
};

const pluralNoun = (outcomes: UploadOutcome[]) => {
  const nouns = new Set(outcomes.map((o) => o.noun));
  const noun = nouns.size === 1 ? outcomes[0].noun : 'file';
  return `${noun}s`;
};

const buildFailureMessage = (
  failed: UploadOutcome[],
  all: UploadOutcome[],
  timedOut: boolean,
) => {
  const savedCount = all.length - failed.length;
  const retryAdvice = 'when you have a stronger connection (Wi-Fi works best).';
  if (savedCount > 0) {
    // The "X of Y" phrase describes the whole batch, so its noun must come
    // from the whole batch (mixed batches collapse to "files").
    return `${savedCount} of ${all.length} ${pluralNoun(all)} uploaded. ${
      failed.length
    } didn't make it - please try uploading ${
      failed.length === 1 ? 'it' : 'those'
    } again ${retryAdvice}`;
  }
  if (failed.length === 1) {
    const {noun, sizeMB} = failed[0];
    if (timedOut && sizeMB >= LARGE_FILE_MB) {
      return `This ${noun} is very large (${sizeMB} MB), and it's taking too long to upload on your current connection. Please try re-uploading it ${retryAdvice}`;
    }
    if (timedOut) {
      return `This ${noun} is taking too long to upload - your connection may be slow right now. Please try re-uploading it ${retryAdvice}`;
    }
    return `This ${noun} didn't upload - there may have been a connection problem. Please try again ${retryAdvice}`;
  }
  if (timedOut) {
    return `Your ${pluralNoun(
      failed,
    )} are taking too long to upload - your connection may be slow right now. Please try re-uploading them ${retryAdvice}`;
  }
  return `Your ${pluralNoun(
    failed,
  )} didn't upload - there may have been a connection problem. Please try again ${retryAdvice}`;
};

// Uploads the selected files, stores an attachment reference for each one that
// makes it, then calls onAttach EXACTLY ONCE with (lastSavedRef | null,
// summary). onAttach always fires - even on total failure or timeout - so
// callers can rely on it to release their loading state. It is never called
// with a blank "photo": a null first argument means "nothing new was saved,
// change nothing". Failures also dispatch UPLOAD_STATUS_EVENT with a
// user-ready message (surfaced globally by UploadFailureSnackbarListener).
export const handleFiles = async (
  event: $TSFixMe,
  onAttach: $TSFixMe,
  attachmentInfo: $TSFixMe,
  files?: $TSFixMe,
) => {
  // Snapshot the list synchronously: the browser nulls event.currentTarget
  // once dispatch completes, so re-reading it after an await crashes
  // multi-file batches partway through.
  const fileList: File[] =
    files ??
    Array.from(event?.currentTarget?.files ?? event?.target?.files ?? []);

  const outcomes: UploadOutcome[] = [];
  const uploadPromises: Promise<void>[] = [];
  let gaveUp = false;
  let finalized = false;
  let watchdog: ReturnType<typeof setTimeout> | undefined;
  let backstop: ReturnType<typeof setTimeout> | undefined;

  const finalize = () => {
    if (finalized) {
      return;
    }
    finalized = true;
    clearTimeout(watchdog);
    clearTimeout(backstop);
    const saved = outcomes.filter((o) => o.status === 'saved');
    const failed = outcomes.filter((o) => o.status !== 'saved');
    const summary: UploadSummary = {
      total: outcomes.length,
      saved: saved.length,
      failed: failed.length,
      timedOut: gaveUp,
    };
    if (failed.length > 0) {
      window.dispatchEvent(
        new CustomEvent(UPLOAD_STATUS_EVENT, {
          detail: {
            message: buildFailureMessage(failed, outcomes, gaveUp),
            ...summary,
          },
        }),
      );
    }
    onAttach(saved.length > 0 ? saved[saved.length - 1].ref : null, summary);
  };

  // The whole setup loop is guarded: if any pre-upload await throws (file
  // read, storage init), the files that never started still get failed
  // outcomes and finalize() still runs - onAttach must ALWAYS fire.
  try {
    for (let i = 0; i < fileList.length; i++) {
      let file: File = fileList[i];

      if (fileList.length === 1) {
        // necessary for Google Drive uploads
        const buffer = await file.arrayBuffer();
        const clone = new File([buffer], file.name, {type: file.type});
        file = clone;
      }

      const uid = sessionStorage.getItem('uid');
      const fileUUID = uuidv4();

      const fileParts = file.name.split('.');
      let extension = fileParts[fileParts.length - 1].toLowerCase();

      // Convert HEIC/HEIF images to JPEG for browser compatibility
      // iPhones use HEIC as the default photo format, but most browsers can't display it
      if (extension === 'heic' || extension === 'heif') {
        try {
          file = await convertHeicToJpeg(file);
          extension = 'jpg';
        } catch (conversionError) {
          console.error('HEIC conversion failed:', conversionError);
          Sentry.withScope(function (scope) {
            scope.setTag('section', 'heic_conversion');
            Sentry.captureException(conversionError);
          });
          // Continue with original file if conversion fails
        }
      }

      const outcome: UploadOutcome = {
        name: file.name,
        sizeMB: Math.max(1, Math.round(file.size / 1048576)),
        noun: fileNoun(extension),
        status: 'pending',
        ref: null,
        task: null,
      };
      outcomes.push(outcome);

      const storePath = `${uid}/${fileUUID}.${extension}`;
      const fileRef = ref(await getStorageInstance(), storePath);

      const metadata = {
        cacheControl: 'max-age=604800',
      };

      const task = uploadBytesResumable(fileRef, file, metadata);
      outcome.task = task;

      const uploadPromise = Promise.resolve(task)
        .then(async () => {
          if (gaveUp) {
            // The user was already told this upload failed - do not store a
            // reference for it, or a later retry would create a duplicate.
            outcome.status = 'canceled';
            return;
          }
          const path = storePath.replace('/', '%2F');
          const attachmentReference = {
            name: file.name,
            url: `${CDN_URL}${path}?alt=media&token=${uuidv4()}`,
            firebaseRef: `${uid}/${fileUUID}`,
            ...attachmentInfo,
          };
          const storeFile = await storeRef(attachmentReference);
          const fileJSON = await storeFile.json();
          if (finalized) {
            // The backstop already reported this file as failed; undo the save
            // so the user's retry can't create a duplicate.
            deleteRef(fileJSON).catch(() => {});
            return;
          }
          outcome.ref = fileJSON;
          outcome.status = 'saved';
        })
        .catch((err: $TSFixMe) => {
          if (err?.code === 'storage/canceled') {
            outcome.status = 'canceled';
            return;
          }
          outcome.status = 'failed';
          process.env.NODE_ENV === 'development' && console.error(err);
          Sentry.withScope(function (scope) {
            scope.setTag('section', 'image_upload');
            Sentry.captureException(err);
          });
        });
      uploadPromises.push(uploadPromise);
    }
  } catch (setupError) {
    process.env.NODE_ENV === 'development' && console.error(setupError);
    Sentry.withScope(function (scope) {
      scope.setTag('section', 'image_upload_setup');
      Sentry.captureException(setupError);
    });
    // Mark files that never started as failed so the user still gets the
    // message and every caller's loading state is still released.
    outcomes.forEach((o) => {
      if (o.status === 'pending' && !o.task) {
        o.status = 'failed';
      }
    });
    for (let j = outcomes.length; j < fileList.length; j++) {
      const skipped = fileList[j];
      const parts = (skipped?.name ?? '').split('.');
      outcomes.push({
        name: skipped?.name ?? 'file',
        sizeMB: Math.max(1, Math.round((skipped?.size ?? 0) / 1048576)),
        noun: fileNoun((parts[parts.length - 1] || '').toLowerCase()),
        status: 'failed',
        ref: null,
        task: null,
      });
    }
  }

  if (outcomes.length === 0) {
    finalize();
    return;
  }

  watchdog = setTimeout(() => {
    gaveUp = true;
    outcomes.forEach((o) => {
      if (o.status === 'pending') {
        // Safe no-op on tasks that already completed.
        o.task?.cancel();
      }
    });
  }, UPLOAD_TIMEOUT_MS);

  backstop = setTimeout(() => {
    outcomes.forEach((o) => {
      if (o.status === 'pending') {
        o.status = 'failed';
      }
    });
    finalize();
  }, FINALIZE_BACKSTOP_MS);

  Promise.allSettled(uploadPromises).then(finalize);
};

export const convertBase64 = (
  files: $TSFixMe,
  onConvert: $TSFixMe,
  trim: $TSFixMe,
) => {
  const file = files[0];
  const reader = new FileReader();
  reader.onloadend = () => {
    let base64 = reader.result;
    if (trim) {
      base64 = (base64 as $TSFixMe).substr(
        (base64 as $TSFixMe).indexOf(',') + 1,
      );
    }
    onConvert(base64, file);
  };
  try {
    reader.readAsDataURL(file);
  } catch (e) {
    process.env.NODE_ENV === 'development' &&
      console.error('Could not use reader.readAsDataURL api');
  }
};

export const downloadURI = (uri: $TSFixMe, name: $TSFixMe) => {
  const link = document.createElement('a');
  link.download = name || '';
  link.href = uri;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  link.remove();
};
